import type { Channel } from './channels';

/**
 * Brand Brain: everything the planner and the writer need to sound like the brand and stay inside
 * its lines. Stored as JSON on the brand row; validated by the zod schemas in contracts.
 */

export type Audience = { id: string; name: string; description: string; pains: string[] };

export type Pillar = {
  id: string;
  name: string;
  description: string;
  /** Relative share of the plan (0 = never planned). */
  weight: number;
  /**
   * The brand's own voice (a founder, a named person): MarketingBee plans the slot and reminds,
   * but never writes or publishes it.
   */
  humanOnly: boolean;
};

export type Guardrails = {
  /** Words or phrases the brand never uses (case-insensitive). */
  bannedPhrases: string[];
  /** Topics to stay away from, given to the writer. */
  avoidTopics: string[];
  /** Block numbers, rankings and superlatives the brand has not backed up (default on). */
  blockUnverifiedClaims: boolean;
  /** Claims the brand can back up; numbers in these are allowed. */
  approvedClaims: string[];
};

export type ChannelCadence = {
  channel: Channel;
  postsPerWeek: number;
  /** Local times to post at, "HH:MM", in preference order. */
  times: string[];
  /** ISO weekdays allowed (1 = Monday ... 7 = Sunday). */
  days: number[];
};

export type BrandProfile = {
  name: string;
  website: string | null;
  /** One paragraph: what the brand sells and to whom. */
  description: string;
  /** How it sounds, e.g. "calm, editorial, specific; never hype". */
  voice: string;
  /** IANA time zone used for the plan. */
  timezone: string;
  /** Languages to write in. `fr` drafts are French (Quebec) versions. */
  languages: ('en' | 'fr')[];
  /** Default destination of calls to action (sign-up page). */
  ctaUrl: string | null;
  /** Shown in every marketing email footer (CASL, CAN-SPAM). Email cannot send without it. */
  mailingAddress: string;
  audiences: Audience[];
  pillars: Pillar[];
  guardrails: Guardrails;
  cadence: ChannelCadence[];
};

export const DEFAULT_GUARDRAILS: Guardrails = { bannedPhrases: [], avoidTopics: [], blockUnverifiedClaims: true, approvedClaims: [] };

export const DEFAULT_TIMES = ['09:30', '12:15', '17:30'];
export const WEEKDAYS = [1, 2, 3, 4, 5];
